import { advisorsSubpath, buildAdvisorsPaths, resolveAdvisorsBase } from '@src/components/sco/Advisors/paths'
import { normalizeAsPath } from '@src/components/sco/Advisors/utils'
import { CATEGORY_KEY_TO_NAME } from '@src/components/sco/Advisors/utils/categoryKeys'
import { BreadcrumbItem } from '@src/contexts/ActiveContentContext'

export const getAdvisorsToolbarBreadcrumbs = (asPath: string): BreadcrumbItem[] => {
  const path = normalizeAsPath(asPath)
  const base = resolveAdvisorsBase(path)
  const paths = buildAdvisorsPaths(base)
  const sub = advisorsSubpath(path, base)

  const withRoot = (...trail: BreadcrumbItem[]): BreadcrumbItem[] => [
    { title: 'Advisors', href: paths.base },
    ...trail,
  ]

  if (sub === '' || sub === '/') {
    return withRoot({ title: 'Home', href: paths.base })
  }

  if (sub === '/ask') {
    return withRoot({ title: 'Ask Wil', href: path })
  }

  if (sub === '/ask-advisors' || sub.startsWith('/ask-advisors/')) {
    return withRoot({ title: 'Ask advisors', href: paths.askAdvisors })
  }

  if (sub === '/my-founders/all') {
    return withRoot(
      { title: 'My founders', href: paths.myFounders },
      { title: 'View all', href: paths.myFoundersAll }
    )
  }

  if (sub === '/my-founders' || sub.startsWith('/my-founders/')) {
    return withRoot({ title: 'My founders', href: paths.myFounders })
  }

  if (sub === '/my-advisors/all') {
    return withRoot(
      { title: 'My Advisors', href: paths.myAdvisors },
      { title: 'All', href: paths.myAdvisorsAll }
    )
  }

  if (sub === '/my-advisors/invited') {
    return withRoot(
      { title: 'My Advisors', href: paths.myAdvisors },
      { title: 'Invited', href: paths.myAdvisorsInvited }
    )
  }

  if (sub === '/my-advisors/searching') {
    return withRoot(
      { title: 'My Advisors', href: paths.myAdvisors },
      { title: 'Searching', href: paths.myAdvisorsSearching }
    )
  }

  if (sub === '/my-advisors/drafts') {
    return withRoot(
      { title: 'My Advisors', href: paths.myAdvisors },
      { title: 'Drafts', href: paths.myAdvisorsDrafts }
    )
  }

  if (sub === '/my-advisors' || sub.startsWith('/my-advisors/')) {
    return withRoot({ title: 'My Advisors', href: paths.myAdvisors })
  }

  // Browse lights "Find Advisors" in the rail (L35), so its crumb sits under Find advisors too (#36).
  if (sub === '/browse') {
    return withRoot({ title: 'Find advisors', href: paths.directory }, { title: 'Browse topics', href: path })
  }

  if (sub === '/directory') {
    return withRoot({ title: 'Find advisors', href: path })
  }

  const directoryCategoryMatch = sub.match(/^\/directory\/([^/]+)$/)
  if (directoryCategoryMatch) {
    const categoryKey = decodeURIComponent(directoryCategoryMatch[1])
    const categoryTitle = CATEGORY_KEY_TO_NAME[categoryKey] || categoryKey
    return withRoot({ title: 'Find advisors', href: paths.directory }, { title: categoryTitle, href: path })
  }

  if (sub === '/apply') {
    return withRoot({ title: 'Apply to advise', href: path })
  }

  if (sub === '/profile') {
    return withRoot({ title: 'My Advisor Profile', href: paths.ownProfile })
  }

  if (sub === '/profile/view') {
    return withRoot(
      { title: 'My Advisor Profile', href: paths.ownProfile },
      { title: 'Profile', href: paths.profileView }
    )
  }

  if (sub === '/profile/edit') {
    return withRoot(
      { title: 'My Advisor Profile', href: paths.ownProfile },
      { title: 'Edit', href: paths.profileEdit }
    )
  }

  if (sub === '/profile/settings') {
    return withRoot(
      { title: 'My Advisor Profile', href: paths.ownProfile },
      { title: 'Settings', href: paths.profileSettings }
    )
  }

  if (sub === '/profile/payouts') {
    return withRoot(
      { title: 'My Advisor Profile', href: paths.ownProfile },
      { title: 'Payouts', href: paths.profilePayouts }
    )
  }

  if (sub === '/profile/reviews') {
    return withRoot(
      { title: 'My Advisor Profile', href: paths.ownProfile },
      { title: 'Reviews', href: paths.profileReviews }
    )
  }

  const profileMatch = sub.match(/^\/profile\/([^/]+)$/)
  if (profileMatch) {
    return withRoot({ title: 'Find advisors', href: paths.directory }, { title: 'Profile', href: path })
  }

  if (sub === '/seats/new') {
    return withRoot({ title: 'My Advisors', href: paths.myAdvisors }, { title: 'Create seat', href: path })
  }

  const seatEditMatch = sub.match(/^\/seats\/([^/]+)\/edit$/)
  if (seatEditMatch) {
    return withRoot({ title: 'My Advisors', href: paths.myAdvisors }, { title: 'Edit seat', href: path })
  }

  const seatDetailMatch = sub.match(/^\/seats\/([^/]+)$/)
  if (seatDetailMatch) {
    return withRoot({ title: 'My Advisors', href: paths.myAdvisors }, { title: 'Seat detail', href: path })
  }

  if (sub === '/opportunities') {
    return withRoot({ title: 'Opportunities', href: path })
  }

  if (sub === '/opportunities/invites') {
    return withRoot(
      { title: 'Opportunities', href: paths.opportunities },
      { title: 'Board invites', href: path }
    )
  }

  if (sub === '/opportunities/applications') {
    return withRoot(
      { title: 'Opportunities', href: paths.opportunities },
      { title: 'Board applications', href: path }
    )
  }

  if (sub === '/opportunities/matches') {
    return withRoot(
      { title: 'Opportunities', href: paths.opportunities },
      { title: 'Matched seats', href: path }
    )
  }

  if (sub === '/opportunities/engagements') {
    return withRoot(
      { title: 'Opportunities', href: paths.opportunities },
      { title: 'Active engagements', href: path }
    )
  }

  const opportunityDetailMatch = sub.match(/^\/opportunities\/([^/]+)$/)
  if (opportunityDetailMatch) {
    return withRoot({ title: 'Opportunities', href: paths.opportunities }, { title: 'Open seat', href: path })
  }

  if (sub === '/become-advisor') {
    return withRoot({ title: 'Become an advisor', href: path })
  }

  if (sub === '/comp-calculator') {
    return withRoot({ title: 'Compensation Calculator', href: path })
  }

  if (sub === '/agreement' || sub.startsWith('/agreements/edit/')) {
    return withRoot(
      { title: 'FAST Agreements', href: paths.agreements },
      { title: 'Edit agreement', href: path }
    )
  }

  if (sub === '/agreements') {
    return withRoot({ title: 'FAST Agreements', href: path })
  }

  if (sub === '/sign') {
    return withRoot(
      { title: 'FAST Agreements', href: paths.agreements },
      { title: 'Sign agreement', href: path }
    )
  }

  if (sub === '/upgrade') {
    return withRoot({ title: 'Upgrade', href: path })
  }

  if (sub === '/sessions' || (sub.startsWith('/sessions/') && sub !== '/sessions/settings')) {
    if (sub === '/sessions/new') {
      return withRoot({ title: 'Sessions', href: paths.sessions }, { title: 'Book session', href: path })
    }
    if (sub === '/sessions' || sub === '/sessions/') {
      return withRoot({ title: 'Sessions', href: paths.sessions })
    }
    if (sub.endsWith('/room')) {
      return withRoot({ title: 'Sessions', href: paths.sessions }, { title: 'Session room', href: path })
    }
    return withRoot({ title: 'Sessions', href: paths.sessions }, { title: 'Session', href: path })
  }

  if (sub === '/sessions/settings') {
    return withRoot(
      { title: 'My Advisor Profile', href: paths.ownProfile },
      { title: 'Settings', href: paths.profileSettings }
    )
  }

  if (sub === '/payouts' || sub.startsWith('/payouts/')) {
    return withRoot(
      { title: 'My Advisor Profile', href: paths.ownProfile },
      { title: 'Payouts', href: paths.profilePayouts }
    )
  }

  return withRoot({ title: 'Advisors', href: paths.base })
}
